import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import type { Theme } from '@mui/material';
import { alpha } from '@mui/material/styles';

import { muiCssBaseline } from 'src/theme/muiCssBaseline';

const muiOverrides = {
  components: {
    MuiCssBaseline: muiCssBaseline,

    MuiSelect: {
      defaultProps: { IconComponent: KeyboardArrowDownIcon },
      styleOverrides: {
        root: ({ theme }: { theme: Theme }) => ({
          '&:hover:not(:disabled)': {
            backgroundColor: theme.colors.neutral[100],
          },
        }),
      },
    },

    MuiButton: {
      defaultProps: {
        disableElevation: true,
        variant: 'contained',
      },

      styleOverrides: {
        root: ({ theme }: { theme: Theme }) => ({
          textTransform: 'none',
          '& .MuiButton-startIcon': {
            marginRight: '0.25rem',
          },
          '& .MuiButton-endIcon': {
            marginLeft: '0.25rem',
          },
          '&.Mui-disabled': {
            backgroundColor: theme.colors.neutral[50],
            color: theme.colors.neutral[200],
          },
          padding: '0.5rem 0.75rem',
          variants: [
            {
              props: { variant: 'cta' },
              style: {
                backgroundColor: theme.colors.primary[500],
                color: theme.colors.white,
                '&:hover:not(:disabled)': {
                  backgroundColor: theme.colors.primary[600],
                },
                '&:active:not(:disabled)': {
                  backgroundColor: theme.colors.primary[700],
                },
              },
            },
            {
              props: { variant: 'cta', color: 'secondary' },
              style: {
                backgroundColor: theme.colors.primary[50],
                color: theme.colors.primary[500],
                '&:hover:not(:disabled)': {
                  backgroundColor: theme.colors.primary[100],
                  color: theme.colors.primary[600],
                },
                '&:active:not(:disabled)': {
                  backgroundColor: theme.colors.primary[200],
                  color: theme.colors.primary[700],
                },
              },
            },
            {
              props: { variant: 'cta', color: 'tertiary' },
              style: {
                backgroundColor: 'unset',
                color: theme.colors.primary[600],
                '&:hover:not(:disabled)': {
                  backgroundColor: theme.colors.primary[50],
                  color: theme.colors.primary[600],
                },
                '&:active:not(:disabled)': {
                  backgroundColor: theme.colors.primary[50],
                  color: theme.colors.primary[700],
                },
              },
            },
            {
              props: { variant: 'ctaAlt' },
              style: {
                backgroundColor: theme.colors.tertiary[400],
                color: theme.colors.tertiary[800],
                '&:hover:not(:disabled)': {
                  backgroundColor: theme.colors.tertiary[500],
                },
                '&:active:not(:disabled)': {
                  backgroundColor: theme.colors.tertiary[600],
                  color: theme.colors.tertiary[900],
                },
              },
            },
            {
              props: { variant: 'ctaBlue' },
              style: {
                backgroundColor: theme.colors.info[500],
                color: theme.colors.white,
                '&:hover:not(:disabled)': {
                  backgroundColor: theme.colors.info[600],
                },
                '&:active:not(:disabled)': {
                  backgroundColor: theme.colors.info[700],
                },
              },
            },
            {
              props: { variant: 'squareCta' },
              style: {
                padding: '0.5rem',
                minWidth: 0,
                backgroundColor: theme.colors.primary[500],
                color: theme.colors.white,
                '&:hover:not(:disabled)': {
                  backgroundColor: theme.colors.primary[600],
                },
                '&:active:not(:disabled)': {
                  backgroundColor: theme.colors.primary[700],
                },
              },
            },
            {
              props: { variant: 'squareCta', color: 'secondary' },
              style: {
                padding: '0.5rem',
                minWidth: 0,
                backgroundColor: theme.colors.primary[50],
                color: theme.colors.primary[500],
                '&:hover:not(:disabled)': {
                  backgroundColor: theme.colors.primary[100],
                  color: theme.colors.primary[600],
                },
                '&:active:not(:disabled)': {
                  backgroundColor: theme.colors.primary[200],
                  color: theme.colors.primary[700],
                },
              },
            },
          ],
        }),
      },
    },

    MuiDivider: {
      styleOverrides: {
        root: ({ theme }: { theme: Theme }) => ({
          borderColor: theme.colors.neutral[200],
          '&:before, &:after': {
            // For dividers with content / text
            borderTop: `2px solid ${theme.colors.neutral[50]}`,
            opacity: 1,
          },
        }),
        wrapper: ({ theme }: { theme: Theme }) => ({
          color: theme.colors.primary[500],
          fontWeight: 700,
          fontSize: 18,
        }),
      },
    },

    MuiIconButton: {
      variants: [
        {
          props: { color: 'primary' },
          style: ({ theme }: { theme: Theme }) => ({
            color: theme.colors.neutral[400],
            minHeight: 'unset',
            minWidth: 'unset',
            padding: 0,
            '&:hover': {
              backgroundColor: 'unset',
              color: theme.colors.primary[500],
            },
          }),
        },
      ],
    },

    MuiTooltip: {
      styleOverrides: {
        tooltip: ({ theme }: { theme: Theme }) => ({
          color: theme.colors.white,
          backgroundColor: theme.colors.neutral[800],
          fontWeight: 600,
          fontSize: '0.75rem',
        }),
        arrow: ({ theme }: { theme: Theme }) => ({
          color: theme.colors.neutral[800],
        }),
      },
    },

    MuiButtonBase: {
      defaultProps: {
        disableRipple: true,
      },
      styleOverrides: {
        root: {
          padding: '0.5rem',
          whiteSpace: 'nowrap',
          minWidth: '2rem',
          minHeight: '2rem',
        },
      },
    },

    MuiCard: {
      styleOverrides: {
        root: {
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
        },
      },
    },

    MuiCheckbox: {
      styleOverrides: {
        root: ({ theme }: { theme: Theme }) => ({
          borderRadius: '2.5px',
          color: theme.colors.neutral[200],
        }),
      },
    },

    MuiLoadingButton: {
      defaultProps: {
        variant: 'contained',
      },
    },

    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
        },
      },
    },

    MuiToggleButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
        },
      },
    },

    MuiTextField: {
      styleOverrides: {
        root: ({ theme }: { theme: Theme }) => ({
          '& .MuiOutlinedInput-root': {
            backgroundColor: theme.colors.neutral[50],
            '&:hover': {
              'input::placeholder': {
                color: theme.colors.neutral[500],
              },
              fieldset: {
                boxShadow: `0 0 2px 1px ${alpha(theme.colors.primary[500], 0.2)}`,
              },
            },
            '&.Mui-focused fieldset': {
              borderColor: theme.colors.primary[200],
              boxShadow: `0 0 2px 1px ${alpha(theme.colors.primary[500], 0.2)}`,
            },
          },
          '& .MuiFormHelperText-root': {
            marginLeft: 0,
            marginRight: 0,
          },
          input: {
            padding: '9px 14px',
            color: theme.colors.neutral[800],
            '&::placeholder': {
              color: theme.colors.neutral[500],
              opacity: 1,
            },
          },
          fieldset: {
            borderWidth: 0,
          },
        }),
      },
    },

    MuiDialog: {
      defaultProps: {
        PaperProps: {
          elevation: 0,
        },
      },
      styleOverrides: {
        root: {
          '& .MuiPaper-root': {
            borderRadius: '8px',
            padding: '18px 32px 42px',
          },
        },
      },
    },

    MuiDialogContent: {
      styleOverrides: {
        root: ({ theme }: { theme: Theme }) => ({
          padding: 0,
          h6: {
            color: theme.colors.neutral[600],
            marginBottom: '8px',
          },
        }),
      },
    },

    MuiDialogContentText: {
      styleOverrides: {
        root: ({ theme }: { theme: Theme }) => ({
          color: theme.colors.neutral[600],
          fontWeight: 600,
          lineHeight: 1.25,
          marginBottom: '8px',
        }),
      },
    },

    MuiDialogTitle: {
      styleOverrides: {
        root: ({ theme }: { theme: Theme }) => ({
          h4: {
            color: theme.colors.neutral[900],
            marginBottom: '4px',
            display: 'inline-block',
          },
          h6: {
            color: theme.colors.neutral[600],
            marginBottom: '8px',
          },
          padding: 0,
        }),
      },
    },

    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: '26px 0 0 0',
          gap: '10px',
        },
      },
    },

    // Affects the language chooser
    MuiMenu: {
      defaultProps: {
        elevation: 0,
      },
    },
  },
};

export default muiOverrides;
